// Glossary client API — PowerSync READ + tRPC mutations.
//
// Список терминов / материалы-с-терминами — локальная SQLite (user_glossary_terms).
// Create/update/delete/generate остаются на tRPC (AI + write через upload-queue).

import { DEFAULT_LOCALE } from "@studyhub/shared";
import type {
	GlossaryMaterialGroup,
	GlossaryTerm,
	GlossaryTermImportance,
	GlossaryTermStatus,
} from "@studyhub/shared/contracts/glossary";
import { useMutation } from "@tanstack/react-query";
import { useMemo } from "react";
import { useCurrentUser } from "@/hooks/use-session";
import { updateRow } from "@/lib/powersync";
import { belongsToLiveMaterial } from "@/lib/powersync/live-material-sql";
import type { LocalGlossaryTerm } from "@/lib/powersync/queries/use-glossary-query";
import { useGlossaryForUserQuery } from "@/lib/powersync/queries/use-glossary-query";
import { usePowerSyncQueryShim } from "@/lib/powersync/query-shim";
import { usePowerSyncQuery } from "@/lib/powersync/use-query";
import { trpcMutation } from "@/lib/trpc-fetch";

export interface CreateGlossaryTermVars {
	materialId?: string;
	term: string;
	definition: string;
	examples?: string[];
	language?: string;
}

/** Manual термин (`glossary.create`). PowerSync подтянет строку в списки. */
export function useCreateGlossaryTerm() {
	return useMutation({
		mutationFn: (vars: CreateGlossaryTermVars) =>
			trpcMutation<{ id: string }>("glossary.create", {
				materialId: vars.materialId,
				term: vars.term,
				definition: vars.definition,
				examples: vars.examples,
				language: vars.language,
			}),
	});
}

export interface UpdateGlossaryTermVars {
	id: string;
	term?: string;
	definition?: string;
	examples?: string[];
}

/** Inline-edit термина — PowerSync PATCH (upload-queue → glossary write-handler). */
export function useUpdateGlossaryTerm() {
	return useMutation({
		mutationFn: async (vars: UpdateGlossaryTermVars) => {
			const patch: {
				definition?: string;
				term?: string;
				examples?: string;
			} = {};
			if (vars.definition !== undefined) patch.definition = vars.definition;
			if (vars.term !== undefined) patch.term = vars.term;
			if (vars.examples !== undefined) patch.examples = JSON.stringify(vars.examples);
			// status выставляет серверный write-handler (зеркало tRPC).
			await updateRow("glossary_terms", vars.id, patch);
			return { ok: true as const };
		},
	});
}

function parseJsonArray(raw: string | null | undefined): unknown[] {
	if (!raw) return [];
	try {
		const parsed: unknown = JSON.parse(raw);
		return Array.isArray(parsed) ? parsed : [];
	} catch {
		return [];
	}
}

function parseExamples(raw: string | null | undefined): string[] {
	return parseJsonArray(raw).filter((x): x is string => typeof x === "string");
}

function parseCitations(raw: string | null | undefined): GlossaryTerm["citations"] {
	return parseJsonArray(raw).flatMap((item) => {
		if (!item || typeof item !== "object") return [];
		const c = item as Record<string, unknown>;
		if (typeof c.chunkId !== "string" || typeof c.snippet !== "string") return [];
		return [
			{
				chunkId: c.chunkId,
				snippet: c.snippet,
				...(typeof c.page === "number" ? { page: c.page } : {}),
				...(typeof c.timestamp === "number" ? { timestamp: c.timestamp } : {}),
			},
		];
	});
}

function parseCrossRefs(raw: string | null | undefined): GlossaryTerm["crossReferences"] {
	return parseJsonArray(raw).flatMap((item) => {
		if (!item || typeof item !== "object") return [];
		const r = item as Record<string, unknown>;
		if (typeof r.materialId !== "string") return [];
		return [{ materialId: r.materialId }];
	});
}

const VALID_IMPORTANCE = new Set<GlossaryTermImportance>(["low", "medium", "high"]);
const VALID_STATUS = new Set<GlossaryTermStatus>(["ai-generated", "verified", "edited", "manual"]);

function adaptGlossaryTerm(row: LocalGlossaryTerm): GlossaryTerm {
	const importance = VALID_IMPORTANCE.has(row.importance as GlossaryTermImportance)
		? (row.importance as GlossaryTermImportance)
		: "medium";
	const status = VALID_STATUS.has(row.status as GlossaryTermStatus)
		? (row.status as GlossaryTermStatus)
		: row.generated_by_ai
			? "ai-generated"
			: "manual";
	return {
		id: row.id,
		userId: row.user_id,
		materialId: row.material_id,
		setId: row.set_id,
		// Термин темы курса (LEARNING-SYSTEM §8): material_id = NULL, привязка идёт
		// через subsection_id. Колонка синкается с миграции 0083.
		subsectionId: row.subsection_id ?? null,
		term: row.term,
		definition: row.definition,
		examples: parseExamples(row.examples),
		occurrences: row.occurrences ?? 0,
		importance,
		citations: parseCitations(row.citations),
		crossReferences: parseCrossRefs(row.cross_references),
		status,
		language: row.language ?? DEFAULT_LOCALE,
		sortOrder: row.sort_order ?? 0,
		provenance:
			row.ai_model || row.prompt_version || row.generation_trace_id
				? {
						aiModel: row.ai_model ?? "unknown",
						promptVersion: row.prompt_version ?? "unknown",
						generationTraceId: row.generation_trace_id ?? "unknown",
						generatedAt: row.created_at,
					}
				: null,
		createdAt: row.created_at,
		updatedAt: row.updated_at,
	};
}

export type GlossarySortBy = "alphabetical" | "importance" | "occurrences" | "createdAt";

const IMPORTANCE_RANK: Record<GlossaryTermImportance, number> = {
	high: 0,
	medium: 1,
	low: 2,
};

function sortTerms(items: GlossaryTerm[], sortBy: GlossarySortBy): GlossaryTerm[] {
	switch (sortBy) {
		case "importance":
			return items.toSorted(
				(a, b) =>
					IMPORTANCE_RANK[a.importance] - IMPORTANCE_RANK[b.importance] ||
					a.term.localeCompare(b.term, undefined, { sensitivity: "base" }),
			);
		case "occurrences":
			return items.toSorted(
				(a, b) =>
					b.occurrences - a.occurrences ||
					a.term.localeCompare(b.term, undefined, { sensitivity: "base" }),
			);
		case "createdAt":
			return items.toSorted((a, b) => b.createdAt.localeCompare(a.createdAt));
		default:
			return items.toSorted((a, b) =>
				a.term.localeCompare(b.term, undefined, { sensitivity: "base" }),
			);
	}
}

/**
 * Глобальный список терминов (PowerSync). Фильтры search/materialId —
 * клиентские (SQLite уже содержит полный набор пользователя).
 */
export function useGlossaryList(
	opts: { sortBy?: GlossarySortBy; search?: string; materialId?: string } = {},
) {
	const user = useCurrentUser();
	const query = useGlossaryForUserQuery(user?.id);
	const data = useMemo(() => {
		let items = (query.data ?? []).map(adaptGlossaryTerm);
		if (opts.materialId) {
			items = items.filter((t) => t.materialId === opts.materialId);
		}
		const q = opts.search?.trim().toLowerCase();
		if (q) {
			items = items.filter(
				(t) => t.term.toLowerCase().includes(q) || t.definition.toLowerCase().includes(q),
			);
		}
		return { items: sortTerms(items, opts.sortBy ?? "alphabetical"), total: items.length };
	}, [query.data, opts.materialId, opts.search, opts.sortBy]);
	return usePowerSyncQueryShim(query, data);
}

interface MaterialGroupRow {
	material_id: string;
	name: string;
	type: string;
	terms_count: number;
}

/**
 * Материалы, у которых есть хотя бы один термин — опции фильтра и группы
 * «По материалам». JOIN с materials (удалённые материалы отсекаем).
 */
export function useGlossaryMaterialGroups() {
	const user = useCurrentUser();
	const query = usePowerSyncQuery<MaterialGroupRow>(
		`SELECT g.material_id AS material_id,
		        COALESCE(m.name, '') AS name,
		        COALESCE(m.type, 'other') AS type,
		        COUNT(*) AS terms_count
		   FROM glossary_terms g
		   LEFT JOIN materials m ON m.id = g.material_id
		  WHERE g.user_id = ?
		    AND g.deleted_at IS NULL
		    AND g.material_id IS NOT NULL
		    AND ${belongsToLiveMaterial("g")}
		GROUP BY g.material_id
		ORDER BY LOWER(COALESCE(m.name, '')) ASC`,
		[user?.id ?? ""],
	);
	const data = useMemo(() => {
		const items: GlossaryMaterialGroup[] = (query.data ?? [])
			.filter((r) => r.material_id && r.name)
			.map((r) => ({
				materialId: r.material_id,
				name: r.name,
				type: r.type,
				termsCount: Number(r.terms_count) || 0,
			}));
		return items;
	}, [query.data]);
	return usePowerSyncQueryShim(query, data);
}
